// Print a radiology report.
//
// The body is the report's own content_html, dropped in untouched — the print
// layer knows nothing about the report's structure, exactly like the editor.
// Only the identification box is ours.
//
// The page itself — A4 box, typography, identification table, page breaking —
// belongs to utils/reportSheet.js, and the letterhead stamped onto every page
// to utils/reportLetterhead.js. Both are shared with the lab report, so the two
// documents are indistinguishable as paper.
import { toast } from "react-toastify";
import { withSalutation } from "../radiologyOrdersShared";
import {
  buildIdBox,
  formatAgeGender,
  formatReportDate,
  renderReportSheet,
} from "../../../utils/reportSheet";
import { resolveFacilityDetails, resolveLogo } from "../../../utils/reportLetterhead";

// Bed, with the ward/room line beside it when the admission carries one — same
// pieces the dashboard's patient row shows.
const formatBed = (patient) => {
  const bed = patient?.bedNo && patient.bedNo !== "-" ? String(patient.bedNo).trim() : "";
  const room = patient?.roomNo && patient.roomNo !== "-" ? String(patient.roomNo).trim() : "";
  if (bed && room) return `${bed} · ${room}`;
  return bed || room || "-";
};

export function buildReportHtml({ patient, order, report }) {
  return `
    <div class="rp-title">Radiology Report</div>
    ${buildIdBox(
      [
        ["Patient Name", patient?.name],
        ["Patient ID", patient?.uhid],
        ["Age / Gender", formatAgeGender(patient?.age, patient?.gender)],
        ["Bed", formatBed(patient)],
        ["Ref. By Doctor", withSalutation(order?.doctor)],
        ["Department", order?.department],
      ],
      [
        // When the study was performed. The scheduled slot is the closest thing
        // the order carries; an unscheduled study falls back to when it was
        // placed.
        ["Exam Date", formatReportDate(order?.scheduled || order?.ordered)],
        ["Report Date", formatReportDate(report?.reported_at || Date.now())],
        ["Accession No.", order?.orderNo],
        ["Modality", order?.modality],
        ["Study Area", order?.service],
      ],
    )}
    <div class="rp-body">${report?.content_html || "<p>No report content.</p>"}</div>
  `;
}

/**
 * Render the report to a PDF Blob.
 *
 * @param {object}  args
 * @param {Array}   args.brandingData   BrandingContext value — supplies the
 *   facility's configurable logo. Omit and the bundled logo is used.
 * @param {object}  args.userInfo       PermissionContext's `userInfo` — supplies
 *   the facility address, the same source the billing invoice prints from.
 * @param {boolean} args.withLetterhead false prints the content alone, for
 *   hospitals feeding pre-printed stationery through the tray.
 * @param {boolean} args.autoPrint      ask the viewer to open its print dialog.
 */
export function generateRadiologyReportPdf({
  patient,
  order,
  report,
  brandingData,
  userInfo,
  withLetterhead = true,
  autoPrint = false,
}) {
  const safeName = String(patient?.name || "Patient").replace(/[^a-zA-Z0-9]/g, "_");
  const { address, contact } = resolveFacilityDetails(userInfo);

  return renderReportSheet({
    html: buildReportHtml({ patient, order, report }),
    filename: `Radiology_Report_${safeName}_${order?.orderNo || ""}.pdf`,
    autoPrint,
    letterhead: withLetterhead
      ? {
          logoBase64: resolveLogo(brandingData),
          address,
          contact,
          patientId: patient?.uhid,
          // The radiologist who wrote it is the only signatory the report
          // carries; "Checked By" stays blank for a counter-signature on paper.
          preparedBy: report?.radiologist_name,
          checkedBy: "",
        }
      : null,
  });
}

export async function printRadiologyReport({
  patient,
  order,
  report,
  brandingData,
  userInfo,
  withLetterhead = true,
}) {
  const filename = reportPdfFilename(patient, order);
  try {
    const blob = await generateRadiologyReportPdf({
      patient,
      order,
      report,
      brandingData,
      userInfo,
      withLetterhead,
      autoPrint: true,
    });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (!win) {
      triggerBlobDownload(blob, filename);
      toast.info("Pop-up blocked — the report PDF was downloaded instead.");
    }
    // Revoking immediately would pull the file out from under the viewer.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    console.error("Could not generate the radiology report PDF:", err);
    toast.error("Could not generate the report PDF. Please try again.");
  }
}

export async function downloadRadiologyReportPdf({
  patient,
  order,
  report,
  brandingData,
  userInfo,
  withLetterhead = true,
}) {
  const filename = reportPdfFilename(patient, order);
  try {
    const blob = await generateRadiologyReportPdf({
      patient,
      order,
      report,
      brandingData,
      userInfo,
      withLetterhead,
      autoPrint: false,
    });
    triggerBlobDownload(blob, filename);
    return blob;
  } catch (err) {
    console.error("Could not download the radiology report PDF:", err);
    toast.error("Could not download the report PDF. Please try again.");
    throw err;
  }
}

function reportPdfFilename(patient, order) {
  const safeName = String(patient?.name || "Patient").replace(/[^a-zA-Z0-9]/g, "_");
  return `Radiology_Report_${safeName}_${order?.orderNo || ""}.pdf`;
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
